import { scheduleConsultationFromSignupV2 } from 'domains/consultations/freeConsultationSchedule';
import { queryClient } from 'domains/data';
import { TherapyTarget } from 'domains/signup/data/types';
import {
  CreateLeadResponseV2,
  createSalesforceLead,
  getSalesforceLead,
  updateSalesforceLead,
} from 'domains/signupv2/Leads/queries';
import { SignupLeadProgressV2, SignupLeadV2 } from 'domains/signupv2/Leads/types';
import { mergeCommunicationConcerns } from 'domains/signupv2/Mini-Screener/data/areaDetection';
import { CompletedSignupV2Data, IncompleteSignupV2Data } from 'domains/signupv2/Parent/signupMachineV2';
import { getGCLIDParameters } from 'domains/tracking/GCLIDtracking';
import { getUTMParameters } from 'domains/tracking/UTMTracking';
import { debounce, isUndefined } from 'lodash';
import { fromPromise } from 'xstate';

export const loadLeadDataLogic = fromPromise<SignupLeadProgressV2 | null, { leadID: string }>(({ input }) => {
  return getSalesforceLead(input.leadID);
});

export const createLeadLogic = fromPromise<CreateLeadResponseV2, CompletedSignupV2Data>(({ input }) => {
  const UTMParams = getUTMParameters();
  const GCLIDParams = getGCLIDParameters();

  return createSalesforceLead({
    address: {
      state: input.state,
    },
    email: input.email,
    firstName: input.firstName,
    lastName: input.lastName,
    phone: input.phoneNumber,

    ...UTMParams,
    ...GCLIDParams,
  });
});

export const scheduleConsultationLogic = fromPromise<void, CompletedSignupV2Data>(({ input }) => {
  return scheduleConsultationFromSignupV2({
    consultationDate: input.scheduleInfo.consultationDate,
    consultationTimezone: input.scheduleInfo.consultationTimezone,
    schedulingId: input.schedulingID,
    appointmentType: input.scheduleParameters.appointmentType,
    calendarID: input.scheduleParameters.calendarID,
  });
});

/** In ms */
const UPDATE_LEAD_DEBOUNCE_TIMER = 2000;

const getDesiredPaymentMethod = (useInsurance?: boolean): SignupLeadV2['desiredPaymentMethod'] | undefined => {
  if (isUndefined(useInsurance)) return undefined;

  return useInsurance ? 'Insurance' : 'Self-pay';
};

export const updateLead = debounce(
  (context: IncompleteSignupV2Data) => {
    const { miniScreenerInfo } = context;
    return queryClient.executeMutation({
      mutationFn: () =>
        updateSalesforceLead({
          id: context.leadID as string,
          schedulingId: context.schedulingID as string,
          address: context.state
            ? {
                state: context.state as string,
              }
            : undefined,
          firstName: context.firstName,
          lastName: context.lastName,
          email: context.email,

          phone: context.phoneNumber,
          serviceFor: context.target,

          communicationConcerns: miniScreenerInfo
            ? mergeCommunicationConcerns(miniScreenerInfo.concerns, context.target as TherapyTarget)
            : undefined,
          detectedAreas: miniScreenerInfo?.detectedAreas,

          desiredPaymentMethod: getDesiredPaymentMethod(context.insuranceInfo?.useInsurance),
        }),
      mutationKey: 'post-signup-lead',
    });
  },
  UPDATE_LEAD_DEBOUNCE_TIMER,
  {
    maxWait: 3000,
    leading: true,
  },
);
